/**
 * CatalogoLayout — Sidebar secundaria de Catálogo + outlet.
 * Cada sección es una URL: /catalogo/destinos, /catalogo/paquetes, etc.
 * Muestra contadores en vivo por sección.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Chip, Divider, Tabs, Tab, Badge } from '@mui/material';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import LuggageIcon from '@mui/icons-material/Luggage';
import SailingIcon from '@mui/icons-material/Sailing';
import AnchorIcon from '@mui/icons-material/Anchor';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.company_id || user.empresa_id || '1';
  } catch { return '1'; }
}
function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// hideForRoles = roles a los que NO se les muestra esta sección.
// Paquetes se oculta a "operaciones" (Fernando): puede ver destinos, alquileres
// y puntos de embarque, pero no debe manipular el catálogo comercial de paquetes.
const ALL_SECTIONS = [
  { path: 'destinos',        label: 'Destinos',        icon: <BeachAccessIcon />,  api: 'destinos',                    color: '#0ea5e9' },
  { path: 'paquetes',        label: 'Paquetes',        icon: <LuggageIcon />,      api: 'paquetes',                    color: '#0d9488', hideForRoles: ['operaciones'] },
  { path: 'alquileres',      label: 'Alquiler de Bote',icon: <SailingIcon />,      api: 'alquiler-embarcacion',        color: '#f59e0b' },
  { path: 'puntos-embarque', label: 'Puntos Embarque', icon: <AnchorIcon />,        api: 'puntos-embarque',            color: '#ec4899' },
];

export default function CatalogoLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const empresaId = getEmpresaId();
  const role = getUserRole();
  const SECTIONS = ALL_SECTIONS.filter(s => !(s.hideForRoles || []).includes(role));
  const [counts, setCounts] = useState({});

  const cargarCounts = useCallback(async () => {
    try {
      const results = await Promise.allSettled(SECTIONS.map(s =>
        fetch(`${API_BASE}/turismo/${s.api}?empresa_id=${empresaId}&solo_activos=false`, { headers: getAuthHeaders() })
          .then(r => r.json())
      ));
      const map = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && Array.isArray(r.value)) {
          map[SECTIONS[i].path] = {
            total: r.value.length,
            inactivos: r.value.filter(x => x.activo === false).length,
          };
        }
      });
      setCounts(map);
    } catch (e) { console.error(e); }
  }, [empresaId]);

  useEffect(() => { cargarCounts(); }, [cargarCounts]);

  // Redirigir al primer tab permitido si entran a /catalogo sin sub-ruta,
  // o si intentan entrar a una sub-ruta que su rol no puede ver (ej: paquetes para operaciones).
  useEffect(() => {
    const primeraDisponible = SECTIONS[0]?.path || 'destinos';
    if (location.pathname === '/catalogo' || location.pathname === '/catalogo/') {
      navigate(`/catalogo/${primeraDisponible}`, { replace: true });
      return;
    }
    const seccionActual = ALL_SECTIONS.find(s => location.pathname.includes(`/catalogo/${s.path}`));
    if (seccionActual && (seccionActual.hideForRoles || []).includes(role)) {
      navigate(`/catalogo/${primeraDisponible}`, { replace: true });
    }
  }, [location.pathname, navigate, role, SECTIONS]);

  const currentSection = SECTIONS.find(s => location.pathname.includes(`/catalogo/${s.path}`));

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Catálogo
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = location.pathname.includes(`/catalogo/${s.path}`);
            const c = counts[s.path];
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/catalogo/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
                {c && c.total > 0 && (
                  <Chip size="small" label={c.total}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: isActive ? s.color : '#e2e8f0',
                          color: isActive ? '#fff' : '#64748b' }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />

        {/* Footer info */}
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Gestiona los datos maestros de tu operación turística. Los cambios afectan
            inmediatamente al chatbot y al panel de booking.
          </Typography>
        </Box>
      </Box>

      {/* Contenido */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Tabs móviles — sólo en xs/sm. Reemplazan el sidebar oculto. */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          mb: 2, mx: -2, px: 2,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: -16, zIndex: 5,
        }}>
          <Tabs
            value={Math.max(0, SECTIONS.findIndex(s => location.pathname.includes(`/catalogo/${s.path}`)))}
            onChange={(_, idx) => navigate(`/catalogo/${SECTIONS[idx].path}`)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.5 },
              '& .MuiTabs-indicator': { backgroundColor: '#0d9488', height: 3, borderRadius: 2 },
            }}
          >
            {SECTIONS.map(s => {
              const c = counts[s.path];
              const n = c?.total || 0;
              return (
                <Tab key={s.path}
                  icon={s.icon} iconPosition="start"
                  label={
                    <Badge badgeContent={n} max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: s.color, color: '#fff',
                          fontSize: 9, height: 16, minWidth: 16, right: -6, top: 2,
                        },
                      }}>
                      <span style={{ paddingRight: n > 0 ? 10 : 0 }}>{s.label}</span>
                    </Badge>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        <Outlet context={{ refreshCounts: cargarCounts, currentSection }} />
      </Box>
    </Box>
  );
}
